import { createReducer } from '@reduxjs/toolkit';
import * as actions from 'actionCreators';
import * as thunkActions from 'SandboxModule/thunks';
import { Realm } from 'types';
import keyBy from 'lodash/keyBy';
import omit from 'lodash/omit';
import { defaultInitialIsoState, DefaultIsoState } from 'IsoStateModule/constants';
import { RealmTypesValues } from 'RealmModule/constants/realm';
import { FetchSandboxSuccessResponse, RealmInfo } from 'SandboxModule/types';

export const defaultSandboxIsoState: DefaultIsoState<string> & FetchSandboxSuccessResponse = {
  ...defaultInitialIsoState,
  count: {
    all: 0,
    standby_available: 0,
  },
  sandbox_info: [],
};

export type InitialStateType = {
  railsSandboxes: Record<string, typeof defaultSandboxIsoState>;
  railsSandboxesHash: Record<string, Realm>;
  dumpFiles: string[];
  requestStatusesAndErrors: {
    [x: string]: {
      // can be serialized
      isRequesting: boolean;
      error: Record<string, any>;
    };
  };
};

export const initialState: InitialStateType = {
  requestStatusesAndErrors: {},
  dumpFiles: [],
  railsSandboxes: {},
  railsSandboxesHash: {},

  // For Testing
  // railsSandboxes: {
  //   [SandboxIsoStateIds.AppbarRealmSelector]: {
  //     topLevelOrder: [],
  //     groupTotalCounts: {
  //       [RealmTypes.sandbox]: 1,
  //       [RealmTypes.subscription]: 1,
  //     },
  //     totalCount: 0,
  //     totalFetchedCount: 2,
  //     ordersByGroup: {
  //       [RealmTypes.sandbox]: ["REGULAR SANDBOX REALM"],
  //       [RealmTypes.subscription]: ["Subscription realm"],
  //     },
  //   },
  // },
  // railsSandboxesHash: keyBy(stub, "realm_id"),
};

export default createReducer(initialState, (builder) => {
  /* ---------------------------- Non Async Request --------------------------- */
  //   builder.addCase(actions.setGeneralIntegrationState, (state, action) => {
  //     state = {
  //       ...state,
  //       ...action.payload,
  //     };
  //   });
  /* --------------------------------Async Request------------------------------------------ */
  builder.addCase(thunkActions.fetchSandboxes.pending, (state, action) => {
    const { arg } = action.meta;
    const { isoStateId } = arg;
    if (isoStateId) {
      // Always set loading state
      state.requestStatusesAndErrors[isoStateId] = {
        isRequesting: true,
        error: {},
      };
      // Initialize iso state only if it doesn't exist
      if (!state.railsSandboxes[isoStateId]) {
        state.railsSandboxes[isoStateId] = { ...defaultSandboxIsoState };
      }
    }
  });
  builder.addCase(thunkActions.fetchSandboxes.fulfilled, (state, action) => {
    // Note that fetchSandboxes are fetching RAILS sandbox
    const { arg } = action.meta;
    const { payload } = action;
    const { isoStateId } = arg;

    // Can use `stub` for testing
    // const sandboxesStub: Sandbox[] = (stub | payload.sandbox_info).map(
    //   (sb: Sandbox, index: number) => ({
    //     ...sb,
    //     realm_type:
    //       index % 2 === 0 ? RealmTypes.sandbox : RealmTypes.subscription,
    //   })
    // );

    // ^ For testing
    const sandboxes: Realm[] = payload.sandbox_info;

    state.railsSandboxesHash = {
      ...state.railsSandboxesHash,
      ...keyBy(sandboxes, 'realm_id'),
    };

    /**
     * Iso state
     */
    if (isoStateId) {
      // Always clear loading state
      state.requestStatusesAndErrors[isoStateId] = {
        isRequesting: false,
        error: {},
      };

      // Update iso state if it exists
      if (state.railsSandboxes[isoStateId]) {
        const ordersByGroup = sandboxes.reduce(
          (acc: Record<RealmTypesValues | string, string[]>, sb) => {
            if (!acc[sb.realm_type]) {
              acc[sb.realm_type] = [sb.realm_id];
            } else {
              acc[sb.realm_type].push(sb.realm_id);
            }
            return acc;
          },
          {},
        );

        state.railsSandboxes[isoStateId] = {
          ...state.railsSandboxes[isoStateId],
          topLevelOrder: sandboxes.map((sb) => sb.realm_id),
          /**
           * Note: the group key here is realm_type
           */
          ordersByGroup,
          ...payload,
        };
      }
    }
  });
  builder.addCase(thunkActions.fetchSandboxes.rejected, (state, action) => {
    const { arg } = action.meta;
    const { isoStateId } = arg;

    if (isoStateId) {
      state.requestStatusesAndErrors[isoStateId] = {
        isRequesting: false,
        error: action.error,
      };
    }
  });
  builder.addCase(thunkActions.deleteRealms.pending, (state, action) => {
    state.requestStatusesAndErrors[thunkActions.deleteRealms.typePrefix] = {
      isRequesting: true,
      error: {},
    };
  });
  builder.addCase(thunkActions.deleteRealms.fulfilled, (state, action) => {
    state.requestStatusesAndErrors[thunkActions.deleteRealms.typePrefix] = {
      isRequesting: false,
      error: {},
    };
    const { arg } = action.meta;
    const realmIds = (arg.realmInfo || []).map((ri: RealmInfo) => ri.realmId);
    state.railsSandboxesHash = omit(state.railsSandboxesHash, realmIds);
  });
  builder.addCase(thunkActions.deleteRealms.rejected, (state, action) => {
    state.requestStatusesAndErrors[thunkActions.deleteRealms.typePrefix] = {
      isRequesting: false,
      error: action.error,
    };
  });
  /* -------------------------------------------------------------------------- */
  builder.addCase(thunkActions.fetchAvailableDumpFiles.pending, (state, action) => {
    state.requestStatusesAndErrors[thunkActions.fetchAvailableDumpFiles.typePrefix] = {
      isRequesting: true,
      error: {},
    };
  });
  builder.addCase(thunkActions.fetchAvailableDumpFiles.fulfilled, (state, action) => {
    state.requestStatusesAndErrors[thunkActions.fetchAvailableDumpFiles.typePrefix] = {
      isRequesting: false,
      error: {},
    };
    state.dumpFiles = (action.payload as any).files;
  });
  builder.addCase(thunkActions.fetchAvailableDumpFiles.rejected, (state, action) => {
    state.requestStatusesAndErrors[thunkActions.fetchAvailableDumpFiles.typePrefix] = {
      isRequesting: false,
      error: {},
    };
  });
  builder.addCase(thunkActions.initializeSandbox.fulfilled, (state, action) => {
    const { status, sandbox } = action.payload as any;
    if (status < 300 && sandbox) {
      state.railsSandboxesHash[sandbox.realm_id] = sandbox;
    }
  });
  builder.addCase(thunkActions.updateSandbox.fulfilled, (state, action) => {
    state.requestStatusesAndErrors[thunkActions.updateSandbox.typePrefix] = {
      isRequesting: false,
      error: {},
    };
    const { updated_sandbox } = (action.payload as any).data;
    state.railsSandboxesHash[updated_sandbox.realm_id] = updated_sandbox;
  });
});
